export interface MapProperty {
  id: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  property_type?: string;
  property_subtype?: string;
  building_class?: string;
  submarket?: string;
  county?: string;
  tenancy?: string;
  zoning?: string;
  opportunity_zone?: boolean;
  status?: string;
  pipeline_status?: string;
  freshness_status?: string;
  building_size?: number;
  lot_size_acres?: number;
  days_on_market?: number;
  price?: number;
  cap_rate?: number;
  latitude?: number;
  longitude?: number;
  primary_photo_url?: string;
  photo_count?: number;
  owner_name?: string;
  year_built?: number;
  rent_per_sf?: number;
  percent_leased?: number;
  // AI comp finder: comparability score (0-100) when returned from that search.
  match_score?: number | null;
  // Distress: most-urgent loan summary (from property_loans) when present.
  loan?: {
    maturity_date: string | null;
    mortgage_amount: number | null;
    lender_name: string | null;
    loan_count: number;
  } | null;
}

export interface MapPermit {
  id: string;
  latitude?: number;
  longitude?: number;
  jurisdiction?: string;
  permit_type?: string;
  status?: string;
  description?: string;
  valuation?: number;
  issue_date?: string;
  is_industrial?: boolean;
  matched_property_id?: string | null;
  source_system?: string;
  permit_number?: string;
  property_address?: string;
}

export interface MapTenant {
  id: string;
  tenant_name: string;
  phone?: string | null;
  contact_name?: string | null;
  sf_occupied?: number | null;
  lease_expiration?: string | null; // ISO date
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  industry?: string | null;
  latitude?: number;
  longitude?: number;
  master_property_id?: string | null;
  ghl_contact_id?: string | null;
}

export interface RadiusCircle {
  id: number;
  lat: number;
  lng: number;
  miles: number;
}

export interface MapFilters {
  // dropdowns
  city: string;
  type: string;
  subtype: string;
  building_class: string;
  submarket: string;
  county: string;
  tenancy: string;
  zoning: string;
  pipeline_status: string;
  // ranges
  min_price: string;
  max_price: string;
  min_building_size: string;
  max_building_size: string;
  min_lot_acres: string;
  max_lot_acres: string;
  year_built_min: string;
  year_built_max: string;
  min_psf: string;
  max_psf: string;
  min_cap: string;
  max_cap: string;
  min_dom: string;
  max_dom: string;
  // toggles ('' | 'yes')
  opportunity_zone: string;
  has_photos: string;
  // expiring tenant window in months ('' | '12' | '24')
  expiring_tenant: string;
}

export interface MapLayers {
  properties: boolean;
  permits: boolean;
  tenants: boolean;
}

export interface SavedSearchCriteria {
  filter: MapFilters;
  circles: RadiusCircle[];
  polygon: [number, number][] | null;
  layers: MapLayers;
  search?: string;
}

export interface SavedSearch {
  id: string;
  name: string;
  criteria: SavedSearchCriteria;
  created_at: string;
}

export const EMPTY_FILTERS: MapFilters = {
  city: '', type: '', subtype: '', building_class: '', submarket: '', county: '', tenancy: '', zoning: '', pipeline_status: '',
  min_price: '', max_price: '', min_building_size: '', max_building_size: '',
  min_lot_acres: '', max_lot_acres: '', year_built_min: '', year_built_max: '',
  min_psf: '', max_psf: '', min_cap: '', max_cap: '', min_dom: '', max_dom: '', opportunity_zone: '', has_photos: '', expiring_tenant: '',
};

// Keys that count as "active" for the Filters button badge.
export const FILTER_KEYS: (keyof MapFilters)[] = [
  'city', 'type', 'subtype', 'building_class', 'submarket', 'county', 'tenancy', 'zoning', 'pipeline_status',
  'min_price', 'max_price', 'min_building_size', 'max_building_size', 'min_lot_acres', 'max_lot_acres',
  'year_built_min', 'year_built_max', 'min_psf', 'max_psf', 'min_cap', 'max_cap', 'min_dom', 'max_dom', 'opportunity_zone', 'has_photos', 'expiring_tenant',
];

export function countActiveFilters(f: MapFilters): number {
  return FILTER_KEYS.reduce((n, k) => n + (f[k] ? 1 : 0), 0);
}
